// Field schemas for the generic CMS content manager.
// kinds: text | textarea | number | toggle | list | pairs | image | select
export const SCHEMAS = {
  product: {
    title: "Products",
    nameField: "name",
    fields: [
      { key: "name", kind: "text", label: "Product Name", required: true },
      { key: "desc", kind: "textarea", label: "Description" },
      { key: "img", kind: "image", label: "Main Image" },
      { key: "specs", kind: "pairs", label: "Specifications (label | value)" },
      { key: "moc", kind: "textarea", label: "Materials of Construction" },
      { key: "applications", kind: "textarea", label: "Applications" },
      { key: "seo_title", kind: "text", label: "SEO Title" },
      { key: "seo_description", kind: "textarea", label: "Meta Description" },
      { key: "active", kind: "toggle", label: "Visible on website" },
    ],
  },
  capability_card: {
    title: "Home Capability Cards",
    nameField: "title",
    fields: [
      { key: "title", kind: "text", label: "Title", required: true },
      { key: "desc", kind: "textarea", label: "Description" },
      { key: "img", kind: "image", label: "Image" },
      { key: "to", kind: "text", label: "Link (e.g. /products)" },
      { key: "active", kind: "toggle", label: "Visible on website" },
    ],
  },
  capability_section: {
    title: "Capabilities Page Sections",
    nameField: "title",
    fields: [
      { key: "kicker", kind: "text", label: "Eyebrow (e.g. Fabrication)", required: true },
      { key: "title", kind: "text", label: "Title", required: true },
      { key: "icon", kind: "select", label: "Icon", options: ["Ruler", "Flame", "FlaskRound", "ShieldCheck"] },
      { key: "img", kind: "image", label: "Image" },
      { key: "img_alt", kind: "text", label: "Image Alt Text" },
      { key: "body", kind: "textarea", label: "Body Text" },
      { key: "bullets", kind: "list", label: "Bullet Points (one per line)" },
      { key: "spec_rows", kind: "pairs", label: "Spec Rows (label | value)" },
      { key: "metrics", kind: "select", label: "Shared Metrics Block", options: ["", "scale", "ndt", "mechanical"] },
      { key: "flip", kind: "toggle", label: "Image on left (flip layout)" },
      { key: "active", kind: "toggle", label: "Visible on website" },
    ],
  },
  scale_group: {
    title: "Engineering Scale Groups",
    nameField: "group",
    fields: [
      { key: "group", kind: "text", label: "Group Title", required: true },
      { key: "items", kind: "pairs", label: "Metrics (label | value)" },
      { key: "active", kind: "toggle", label: "Visible on website" },
    ],
  },
  facility_gallery: {
    title: "Facilities Gallery",
    nameField: "alt",
    fields: [
      { key: "img", kind: "image", label: "Image", required: true },
      { key: "alt", kind: "text", label: "Alt Text / Caption", required: true },
      { key: "active", kind: "toggle", label: "Visible on website" },
    ],
  },
  facility_logistics: {
    title: "Logistics Cards",
    nameField: "title",
    fields: [
      { key: "title", kind: "text", label: "Title", required: true },
      { key: "icon", kind: "select", label: "Icon", options: ["Truck", "Anchor", "Route"] },
      { key: "desc", kind: "textarea", label: "Description" },
      { key: "spec_rows", kind: "pairs", label: "Spec Rows (label | value)" },
      { key: "active", kind: "toggle", label: "Visible on website" },
    ],
  },
  facility_bay: {
    title: "Fabrication Bays",
    nameField: "bay",
    fields: [
      { key: "bay", kind: "text", label: "Bay Name", required: true },
      { key: "size", kind: "text", label: "Size", required: true },
      { key: "active", kind: "toggle", label: "Visible on website" },
    ],
  },
  leader: {
    title: "Leadership",
    nameField: "name",
    fields: [
      { key: "name", kind: "text", label: "Name", required: true },
      { key: "role", kind: "text", label: "Designation", required: true },
      { key: "img", kind: "image", label: "Photograph" },
      { key: "bio", kind: "textarea", label: "Short Bio (optional)" },
      { key: "active", kind: "toggle", label: "Visible on website" },
    ],
  },
  nav_item: {
    title: "Navigation Items",
    nameField: "label",
    fields: [
      { key: "label", kind: "text", label: "Label", required: true },
      { key: "to", kind: "text", label: "Link (e.g. /products)", required: true },
      { key: "parent", kind: "text", label: "Parent item ID (for dropdown; blank = top level)" },
      { key: "area", kind: "select", label: "Area", options: ["header", "footer"] },
      { key: "active", kind: "toggle", label: "Visible on website" },
    ],
  },
  home: {
    title: "Home Page",
    singleton: true,
    fields: [
      { key: "hero_title", kind: "textarea", label: "Hero Heading (one line per row; last line shows in red)" },
      { key: "hero_sub", kind: "textarea", label: "Hero Subheading" },
      { key: "hero_img", kind: "image", label: "Hero Background Image" },
      { key: "hero_badges", kind: "list", label: "Hero Badges (one per line; first = outline, rest = red)" },
      { key: "hero_metrics", kind: "pairs", label: "Hero Metric Bar (value | label)" },
      { key: "cta_primary_text", kind: "text", label: "Primary Button Text" },
      { key: "cta_primary_link", kind: "text", label: "Primary Button Link" },
      { key: "cta_secondary_text", kind: "text", label: "Secondary Button Text" },
      { key: "cta_secondary_link", kind: "text", label: "Secondary Button Link" },
      { key: "show_stats", kind: "toggle", label: "Show stats strip" },
      { key: "show_about", kind: "toggle", label: "Show about preview" },
      { key: "show_scale", kind: "toggle", label: "Show engineering scale" },
      { key: "show_capabilities", kind: "toggle", label: "Show capability cards" },
      { key: "show_services", kind: "toggle", label: "Show services" },
      { key: "show_projects", kind: "toggle", label: "Show featured projects" },
      { key: "show_industries", kind: "toggle", label: "Show industries" },
      { key: "show_quality", kind: "toggle", label: "Show quality section" },
      { key: "show_clients", kind: "toggle", label: "Show clients strip" },
      { key: "show_careers", kind: "toggle", label: "Show careers section" },
    ],
  },
  contact: {
    title: "Contact Information",
    singleton: true,
    fields: [
      { key: "phone", kind: "text", label: "Phone" },
      { key: "email", kind: "text", label: "Sales Email" },
      { key: "domain", kind: "text", label: "Domain" },
      { key: "address_office", kind: "list", label: "Head Office Address (one line per row)" },
      { key: "address_factory", kind: "list", label: "Factory Address (one line per row)" },
      { key: "maps_url", kind: "text", label: "Google Maps Link (optional)" },
      { key: "social_links", kind: "pairs", label: "Social Links (label | URL)" },
    ],
  },
  navigation_meta: {
    title: "Header & Footer Settings",
    singleton: true,
    fields: [
      { key: "cta_text", kind: "text", label: "Header CTA Button Text" },
      { key: "cta_link", kind: "text", label: "Header CTA Button Link" },
      { key: "footer_tagline", kind: "textarea", label: "Footer Company Text" },
      { key: "footer_note", kind: "text", label: "Footer Bottom Note" },
    ],
  },
};
